import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";

export default function LoginPage() {
  const { login, guestLogin, verifyTwoFactor, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [guestName, setGuestName] = useState("");
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password, rememberMe);
      if (result.requires_2fa) {
        setMfaToken(result.mfa_token);
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTwoFactor = async (e) => {
    e.preventDefault();
    if (mfaCode.trim().length !== 6) return toast.error("Enter the 6-digit code from your authenticator app");
    setLoading(true);
    try {
      await verifyTwoFactor(mfaToken, mfaCode.trim(), rememberMe);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleGuest = async () => {
    if (!guestName.trim()) {
      toast.error("Enter a name to join as guest");
      return;
    }
    setLoading(true);
    try {
      await guestLogin(guestName);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#151613] text-[#f1e0c5] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="font-display text-2xl flex items-center justify-center gap-2.5 mb-10">
          <img src="/logo192.png" alt="Call to Arms" className="w-8 h-8 rounded-md" data-testid="brand-logo-icon" />
          Call to Arms<span className="text-[#D90429]">.</span>
        </Link>
        <div className="panel-wood rounded-lg p-8">
          {mfaToken ? (
            <>
              <h1 className="font-display text-3xl mb-2">Two-Factor Check</h1>
              <p className="text-sm text-[#f1e0c5]/60 mb-6">Enter the 6-digit code from your authenticator app.</p>
              <form onSubmit={handleVerifyTwoFactor} className="space-y-4">
                <Input
                  data-testid="login-2fa-code-input"
                  autoFocus
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="bg-black/30 border-white/10 text-center text-2xl tracking-[0.4em]"
                />
                <Button data-testid="login-2fa-verify-button" type="submit" disabled={loading} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f] uppercase tracking-widest text-sm">
                  Verify
                </Button>
                <button data-testid="login-2fa-back-button" type="button" onClick={() => { setMfaToken(null); setMfaCode(""); }} className="w-full text-xs text-[#f1e0c5]/50 hover:text-[#D4AF37]">
                  Back to login
                </button>
              </form>
            </>
          ) : (
          <>
          <h1 className="font-display text-3xl mb-6">Welcome back</h1>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-[#f1e0c5]/60">Email</Label>
              <Input data-testid="login-email-input" id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 bg-black/30 border-white/10" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-[#f1e0c5]/60">Password</Label>
              <Input data-testid="login-password-input" id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1 bg-black/30 border-white/10" />
            </div>
            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center gap-2">
                <Checkbox data-testid="login-remember-me-checkbox" id="remember" checked={rememberMe} onCheckedChange={setRememberMe} />
                <Label htmlFor="remember" className="text-xs text-[#f1e0c5]/60 cursor-pointer">Keep me signed in on this device</Label>
              </div>
              <Link to="/forgot-password" data-testid="login-forgot-password-link" className="text-xs text-[#D4AF37] hover:underline shrink-0">Forgot?</Link>
            </div>
            <Button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f] uppercase tracking-widest text-sm">
              Log In
            </Button>
          </form>
          <p className="text-sm text-center mt-4 text-[#f1e0c5]/60">
            No account?{" "}
            <Link to="/register" data-testid="login-register-link" className="text-[#D4AF37] hover:underline">
              Create one
            </Link>
          </p>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs uppercase tracking-widest text-[#f1e0c5]/40">or join as guest</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          <div className="flex gap-2">
            <Input
              data-testid="login-guest-name-input"
              placeholder="Your name"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              className="bg-black/30 border-white/10"
            />
            <Button data-testid="login-guest-button" onClick={handleGuest} disabled={loading} variant="secondary" className="rounded-full whitespace-nowrap">
              Join as Guest
            </Button>
          </div>
          </>
          )}
        </div>

        <a href="https://wwwwhynot.com" target="_blank" rel="noreferrer" data-testid="wwwwhynot-logo-link" className="mt-6 flex justify-center opacity-80 hover:opacity-100 transition-opacity duration-200">
          <img src="https://customer-assets-39nsmqrw.emergentagent.net/job_adventure-hub-220/artifacts/dyv5y9i0_ChatGPT%20Image%20Jun%208%2C%202026%2C%2006_17_24%20PM.png" alt="wwwwhynot.com" width={28} height={28} className="rounded-md object-contain" />
        </a>
      </div>
    </div>
  );
}
